import type { MemberProgramCardData } from './types';

/**
 * Which program the learner dashboard's Continue pill points at.
 *
 * The first program the learner can resume, in the order they see on the board:
 * columns left to right by pillar order, cards top to bottom within a column.
 * Pass the programs already in that order.
 *
 * It used to prefer any in-progress program over a not-started one, wherever
 * each sat on the board. That is why a learner whose first column started with
 * a priority program at 0% was still pointed at a half-finished one further
 * down: the pill disagreed with the board, and an admin had no way to steer it.
 * Order now decides, so arranging the columns arranges the CTA.
 *
 * A completed program is never offered, and neither is a locked one.
 *
 * `groups.locked` is an admin toggle (PATCH /group/{grpId}/lock). While it is
 * set, the backend answers every learner read on that program with HTTP 423:
 * getElliottLesson, checkElliottQuizAnswer, getElliottImproveQueue and
 * openElliottSimulationRetake all refuse. So a locked program offered as the
 * resume target is a CTA that walks the learner straight into a wall — the
 * program card already disables its own Continue for exactly this reason
 * (MemberProgramCard, locked badge + tooltip); this applies the same rule to
 * the one CTA that skipped it.
 *
 * A program the pill cannot open a lesson in is stepped over rather than
 * chosen and then discarded. Only getMemberLearningFramework computes
 * `resumeLesson`, and it returns null for a program with no lesson tree of its
 * own — a mentorship program, for instance. Picking one of those used to end
 * the search, so a single mentorship card at the top of the board hid the pill
 * from a learner whose course below it was perfectly resumable.
 *
 * When NO program on the board can be deep-linked into, the first resumable one
 * is still returned: its card opens the program drawer, which is a real
 * destination. Null means the learner genuinely has nothing to resume —
 * everything is locked or complete — and the bar then renders no pill, which is
 * the correct outcome: no CTA beats a dead one.
 */
export type ResumeCandidate = Pick<MemberProgramCardData, 'status' | 'locked'> & {
  resumeLesson?: { moduleId?: string; taskId?: string } | null;
};

const isResumable = (p: ResumeCandidate): boolean => p.locked !== true && p.status !== 'completed';

const opensALesson = (p: ResumeCandidate): boolean => Boolean(p.resumeLesson?.moduleId && p.resumeLesson?.taskId);

export function pickResumeProgram<T extends ResumeCandidate>(programs: T[] | null | undefined): T | null {
  if (!Array.isArray(programs)) return null;
  const resumable = programs.filter((p) => !!p && isResumable(p));
  return resumable.find(opensALesson) ?? resumable[0] ?? null;
}

/**
 * Sort one column's cards the way the board shows them: the admin's manual
 * order first (groups.displayOrder, set by dragging on the LL admin dashboard),
 * then anything never dragged, in the order the API returned. Stable, so two
 * cards with the same position keep their relative order.
 */
export function byDisplayOrder<T extends Pick<MemberProgramCardData, 'displayOrder'>>(programs: T[]): T[] {
  return programs
    .map((p, i) => ({ p, i }))
    .sort((a, b) => {
      const av = typeof a.p?.displayOrder === 'number' ? a.p.displayOrder : Number.MAX_SAFE_INTEGER;
      const bv = typeof b.p?.displayOrder === 'number' ? b.p.displayOrder : Number.MAX_SAFE_INTEGER;
      return av - bv || a.i - b.i;
    })
    .map((x) => x.p);
}
